/**
 * Browser file-saving helpers.
 *
 * Attachments are fetched here rather than linked with `<a href download>`.
 * A link click is a *navigation* request, and Cloudflare's static-asset router
 * (`not_found_handling = "single-page-application"` in wrangler.toml) answers
 * navigation requests with index.html before the Worker ever runs — so every
 * attachment "download" saved the SPA shell as `download`, `download (1)`, …
 * regardless of what the API would have returned. A fetch is a cors-mode
 * request, which the asset router passes through to the Worker untouched.
 *
 * Fetching also means a failed download surfaces as an error instead of
 * silently writing a wrong file to disk.
 */

/** Hand a blob to the browser as a file save, then release the object URL. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Deferred: revoking in the same tick can cut off a download the browser has
  // not started reading yet, which bites on multi-megabyte attachments in a way
  // it never did on the small CSV templates this pattern started with.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/**
 * Fetch an attachment from the API and save it under `filename` — the name the
 * upload was stored with, which the attachment row already carries. Throws on
 * a non-2xx so the caller can surface it.
 */
export async function downloadAttachment(url: string, filename: string): Promise<void> {
  const res = await fetch(url, { credentials: 'include' })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as any)
    throw new Error((body as any)?.error?.message || `Download failed (${res.status})`)
  }
  saveBlob(await res.blob(), filename)
}
